
import { useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { IconFlame, IconClock, IconStar, IconTrending, IconFilter } from "@/components/ui/Icons";
import type { SortOrder, TimeRange } from "@/types";

interface FeedHeaderProps {
  isAuthenticated: boolean;
  hasSubscriptions: boolean;
  onSortChange?: (sort: SortOrder) => void;
  onTimeRangeChange?: (range: TimeRange) => void;
}

const sortOptions: { value: SortOrder; label: string; icon: React.ReactNode }[] = [
  { value: "hot", label: "Hot", icon: <IconFlame className="w-4 h-4" /> },
  { value: "new", label: "New", icon: <IconClock className="w-4 h-4" /> },
  { value: "top", label: "Top", icon: <IconStar className="w-4 h-4" /> },
  { value: "rising", label: "Rising", icon: <IconTrending className="w-4 h-4" /> },
];

const timeRanges: { value: TimeRange; label: string }[] = [
  { value: "hour", label: "Past hour" },
  { value: "day", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
];

export function FeedHeader({ isAuthenticated, hasSubscriptions, onSortChange, onTimeRangeChange }: FeedHeaderProps) {
  const [activeSort, setActiveSort] = useState<SortOrder>("hot");
  const [timeRange, setTimeRange] = useState<TimeRange>("day");
  const [showTimeRange, setShowTimeRange] = useState(false);

  const handleSortChange = (sort: SortOrder) => {
    setActiveSort(sort);
    setShowTimeRange(sort === "top");
    onSortChange?.(sort);
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    onTimeRangeChange?.(range);
  };

  return (
    <div className="mb-4 space-y-2">
      {/* Sort tabs */}
      <div className="flex items-center gap-1 bg-surface-overlay rounded-2xl p-1 border border-border">
        {sortOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSortChange(option.value)}
            className={cn(
              "relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-150 flex-1 justify-center",
              activeSort === option.value
                ? "text-foreground"
                : "text-text-muted hover:text-foreground"
            )}
          >
            {activeSort === option.value && (
              <motion.div
                layoutId="feed-sort-pill"
                className="absolute inset-0 bg-surface-elevated rounded-xl shadow-sm border border-border"
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1.5">
              {option.icon}
              <span className="hidden sm:inline">{option.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Time range filter (for Top sort) */}
      {showTimeRange && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1"
        >
          <IconFilter className="w-4 h-4 text-text-subtle flex-shrink-0" />
          {timeRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => handleTimeRangeChange(range.value)}
              className={cn(
                "text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-all duration-150 flex-shrink-0",
                timeRange === range.value
                  ? "bg-brand text-brand-foreground border-brand"
                  : "border-border text-text-muted hover:border-brand/50 hover:text-foreground"
              )}
            >
              {range.label}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
}
